import {
  type ColumnCategory,
  findCategoryIdForValue,
} from "src/formkit/components/mapper-v2/column-categories";
import { MappingType } from "src/formkit/components/types";

export type OtherValueType =
  | MappingType.STATIC
  | MappingType.VARIABLE
  | MappingType.TEMPLATE
  | MappingType.OBJECT
  | MappingType.ARRAY;

export const OTHER_VALUE_TYPES: OtherValueType[] = [
  MappingType.STATIC,
  MappingType.VARIABLE,
  MappingType.TEMPLATE,
  MappingType.OBJECT,
  MappingType.ARRAY,
];

export type NavSelection =
  | { kind: "column"; categoryId: string | undefined }
  | { kind: "other"; type: OtherValueType };

/** The mapping value the popover edits. Which fields are set depends on `type`. */
export type MapperValue = {
  type?: MappingType;
  from?: unknown;
  to?: unknown;
  alias?: string;
  value?: unknown;
  valueType?: string;
  variable?: string;
  template?: string;
  children?: unknown;
};

type EnabledValueTypesInput = {
  /** A form-wide restriction on the mapping types, such as a journey tile's. */
  mappingTypesOverride: MappingType[] | undefined;
  /** The mapping types the destination's formkit definition enables for this field. */
  fieldMappingTypes: MappingType[] | undefined;
  hasModel: boolean;
  enableInLineMapper: boolean | undefined;
  /** The destination field's extended type, e.g. "OBJECT" or "ARRAY". */
  destinationFieldType: string | undefined;
};

export function getEnabledValueTypes({
  mappingTypesOverride,
  fieldMappingTypes,
  hasModel,
  enableInLineMapper,
  destinationFieldType,
}: EnabledValueTypesInput): ReadonlySet<MappingType> {
  // A form-wide restriction (e.g. journey sync nodes, whose realtime worker
  // only supports trait columns, static values, and variables) beats whatever
  // the destination's formkit definition enables per field.
  const mappingTypes = mappingTypesOverride ?? fieldMappingTypes;
  const isRestricted = Boolean(mappingTypes?.length);
  const allows = (type: MappingType) =>
    isRestricted ? Boolean(mappingTypes?.includes(type)) : true;

  const enabled = new Set<MappingType>();
  if (allows(MappingType.STANDARD)) enabled.add(MappingType.STANDARD);
  if (allows(MappingType.STATIC)) enabled.add(MappingType.STATIC);
  if (isRestricted ? allows(MappingType.VARIABLE) : hasModel) {
    enabled.add(MappingType.VARIABLE);
  }
  if (allows(MappingType.TEMPLATE)) enabled.add(MappingType.TEMPLATE);
  if (
    enableInLineMapper &&
    (destinationFieldType === "OBJECT" || !destinationFieldType) &&
    allows(MappingType.OBJECT)
  ) {
    enabled.add(MappingType.OBJECT);
  }
  if (
    enableInLineMapper &&
    (destinationFieldType === "ARRAY" || !destinationFieldType) &&
    allows(MappingType.ARRAY)
  ) {
    enabled.add(MappingType.ARRAY);
  }
  return enabled;
}

/** Boosted values and values without a type are column mappings. */
export function getValueType(value: MapperValue | undefined): MappingType {
  const type = value?.type;
  return !type || type === MappingType.BOOSTED ? MappingType.STANDARD : type;
}

function isOtherValueType(type: MappingType): type is OtherValueType {
  return OTHER_VALUE_TYPES.some((otherType) => otherType === type);
}

function getSelectionType(selection: NavSelection): MappingType {
  return selection.kind === "column" ? MappingType.STANDARD : selection.type;
}

/** A stable key for a nav item, unique across columns and other values. */
export function getNavItemKey(selection: NavSelection): string {
  return selection.kind === "column"
    ? `column-${selection.categoryId ?? "none"}`
    : `other-${selection.type}`;
}

/** The id of a nav item's tab, which the tab panel's `aria-labelledby` references. */
export function getNavTabId(idPrefix: string, selection: NavSelection): string {
  return `${idPrefix}-tab-${getNavItemKey(selection)}`;
}

export function isSameSelection(a: NavSelection, b: NavSelection): boolean {
  return getNavItemKey(a) === getNavItemKey(b);
}

export function getInitialSelection({
  value,
  enabledTypes,
  categories,
}: {
  value: MapperValue | undefined;
  enabledTypes: ReadonlySet<MappingType>;
  categories: ColumnCategory[];
}): NavSelection {
  const valueType = getValueType(value);
  const canPickColumns =
    enabledTypes.has(MappingType.STANDARD) && categories.length > 0;

  if (valueType === MappingType.STANDARD && canPickColumns) {
    return {
      kind: "column",
      categoryId:
        findCategoryIdForValue(categories, value?.from) ?? categories[0]?.id,
    };
  }
  if (isOtherValueType(valueType) && enabledTypes.has(valueType)) {
    return { kind: "other", type: valueType };
  }
  if (canPickColumns) {
    return { kind: "column", categoryId: categories[0]?.id };
  }
  const firstOther = OTHER_VALUE_TYPES.find((type) => enabledTypes.has(type));
  if (firstOther) {
    return { kind: "other", type: firstOther };
  }
  // Nothing to choose from: the column pane explains why it is empty.
  return { kind: "column", categoryId: undefined };
}

/**
 * Keeps the draft when the selection edits the same mapping type, and starts a
 * fresh value of the new type otherwise.
 */
export function getDraftForSelection(
  draft: MapperValue,
  selection: NavSelection,
): MapperValue {
  const selectionType = getSelectionType(selection);
  return getValueType(draft) === selectionType
    ? draft
    : { type: selectionType };
}

export const ARRAY_COLUMN_REQUIRED_MESSAGE =
  "Please select a column before proceeding.";
export const ARRAY_COLUMN_MISMATCH_MESSAGE =
  "You must select the same column as the top-level mapping.";

export function resolveAppliedValue({
  draft,
  valueToApply,
  parentMapping,
}: {
  draft: MapperValue;
  valueToApply: MapperValue;
  parentMapping: Pick<MapperValue, "type" | "from"> | undefined;
}): { ok: true; value: MapperValue } | { ok: false; message: string } {
  const value = { ...draft, ...valueToApply };

  if (value.type === MappingType.ARRAY && !value.from) {
    return { ok: false, message: ARRAY_COLUMN_REQUIRED_MESSAGE };
  }
  if (
    parentMapping?.type === MappingType.ARRAY &&
    value.type === MappingType.ARRAY &&
    parentMapping.from !== value.from
  ) {
    return { ok: false, message: ARRAY_COLUMN_MISMATCH_MESSAGE };
  }
  // "boosted" only exists in the mapper; the saved mapping is a column mapping.
  if (value.type === MappingType.BOOSTED) {
    return { ok: true, value: { ...value, type: MappingType.STANDARD } };
  }
  return { ok: true, value };
}
